import type { OpeningHours, Product } from './types'

/** Format ören (cents) to Swedish price string */
export function fmt(ore: number): string {
  return `${Math.round(ore / 100)} kr`
}

/** Generate a UUID v4 */
export function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/** Parse ISO 8601 duration like PT15M to minutes */
export function parseDurationMinutes(iso: string | null): number {
  if (!iso) return 15
  const m = iso.match(/PT(?:(\d+)H)?(?:(\d+)M)?/)
  if (!m) return 15
  return parseInt(m[1] || '0', 10) * 60 + parseInt(m[2] || '0', 10)
}

/** Format a Date to HH:MM */
export function fmtTime(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

const DAY_MAP: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
}

/** Check if a product's opening hours indicate it's available now */
export function isOpenNow(oh: OpeningHours | null): boolean {
  if (!oh) return true
  if (oh.productVisibility === 'AlwaysVisible') return true
  const now = new Date()
  const day = now.getDay()
  const t = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:00`
  return oh.periods.some((p) => {
    if (DAY_MAP[p.open.day] !== day) return false
    return t >= p.open.time && t <= (p.close?.time || '23:59:59')
  })
}

/** Check if a product is available for ordering */
export function isAvailable(p: Product): boolean {
  if (!p.properties) return false
  if (p.properties.temporarilyDisabled || !p.properties.isActive) return false
  // Allow price=0 if product has alternatives with prices (size-based products)
  if (!p.properties.price && !hasPricedAlternatives(p.properties.alternativeGroups)) return false
  return isOpenNow(p.properties.openingHours)
}

/** Check if alternatives contain priced options (size selection etc) */
function hasPricedAlternatives(
  groups:
    | {
        alternativeItems: { addonProduct: { properties: { price: number | null } | null } | null }[]
      }[]
    | null,
): boolean {
  if (!groups) return false
  return groups.some((g) => g.alternativeItems.some((ai) => (ai.addonProduct?.properties?.price || 0) > 0))
}

/** Minimum price for a product — either base price or lowest addon price if base is 0 */
export function minPrice(p: Product): number {
  const base = p.properties?.price || 0
  if (base > 0) return base
  const groups = p.properties?.alternativeGroups || []
  let min = Infinity
  for (const g of groups) {
    for (const ai of g.alternativeItems) {
      const ap = ai.addonProduct?.properties?.price
      if (ap && ap > 0 && ap < min) min = ap
    }
  }
  return min === Infinity ? 0 : min
}
